import { useEffect, useRef, useState, type FormEvent } from "react";
import { Construction, Link2, LoaderCircle, LockOpen, UserRound } from "lucide-react";
import { ApiError, exchangeDeviceLinkSession, loadRegistrationPolicy, login, register, requestDeviceLinkApproval, type RegistrationMode } from "../api";
import { createDeviceLinkClaimProof, readDeviceLink } from "../recovery";
import {
  createIdentity,
  appLockPreference,
  devicePayload,
  prepareIdentity,
  saveIdentity,
  saveIdentityAutomatic,
  type VaultProtection,
} from "../vault";
import type { DeviceIdentity, Session } from "../types";

type Props = Readonly<{
  onAuthenticated: (session: Session, identity: DeviceIdentity, protection: VaultProtection) => void;
}>;

type PendingApproval = Readonly<{
  approvalCode: string;
  claimId: string;
  claimToken: string;
  identity: DeviceIdentity;
}>;

export function AuthScreen({ onAuthenticated }: Props) {
  const [deviceLink] = useState(() => readDeviceLink(window.location.hash));
  const [mode, setMode] = useState<"login" | "register">("login");
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode | "loading">("loading");
  const [username, setUsername] = useState(() => localStorage.getItem("jtuc:last-user") ?? "");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localUnlock, setLocalUnlock] = useState("");
  const [pendingApproval, setPendingApproval] = useState<PendingApproval | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const polling = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void loadRegistrationPolicy().then((value) => {
      if (!cancelled) setRegistrationMode(value);
    }).catch(() => {
      // Login remains available when policy cannot be loaded; the server is
      // authoritative and the public registration tab stays hidden.
      if (!cancelled) setRegistrationMode("closed");
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!deviceLink || !pendingApproval) return;
    let cancelled = false;
    const poll = async () => {
      if (polling.current || cancelled) return;
      polling.current = true;
      try {
        const result = await exchangeDeviceLinkSession(deviceLink.linkId, pendingApproval.claimId, pendingApproval.claimToken);
        if (cancelled) return;
        if (result.state === "pending") {
          setError(null);
          return;
        }
        await saveIdentityAutomatic(result.user.username, pendingApproval.identity);
        localStorage.setItem("jtuc:last-user", result.user.username);
        onAuthenticated(result, pendingApproval.identity, "automatic");
      } catch (caught) {
        if (cancelled) return;
        const terminal = caught instanceof ApiError && [403, 404, 409].includes(caught.status);
        setError(terminal
          ? caught.message
          : "Связь с сервером временно потеряна. Продолжаем ожидать подтверждение…");
        if (terminal) {
          setPendingApproval(null);
        }
      } finally {
        polling.current = false;
      }
    };
    const timer = window.setInterval(() => void poll(), 2_000);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceLink, onAuthenticated, pendingApproval]);

  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (mode === "register" && registrationMode !== "open") {
      setMode("login");
      setError("Самостоятельная регистрация закрыта. Обратитесь к администратору.");
      return;
    }
    if (password.length < 12) {
      setError("Пароль должен содержать не менее 12 символов");
      return;
    }
    if (mode === "register" && password !== confirm) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    try {
      const explicitLock = appLockPreference(username);
      if (explicitLock === "passphrase" && localUnlock.length < 12) {
        throw new Error("Введите локальную защитную фразу этого устройства");
      }
      const prepared = await prepareIdentity(username, explicitLock === "passphrase" ? localUnlock : password);
      const payload = {
        username,
        password,
        ...(mode === "register" ? { displayName } : {}),
        device: devicePayload(prepared.identity),
      };
      const session = mode === "register" ? await register(payload) : await login(payload);
      const identity = session.deviceId === prepared.identity.id ? prepared.identity : { ...prepared.identity, id: session.deviceId };
      const migrateLegacyProtection = explicitLock === null
        && (prepared.protection === "passphrase" || prepared.protection === "system");
      if (prepared.protection === "none" || prepared.protection === "automatic" || migrateLegacyProtection) {
        await saveIdentityAutomatic(username, identity);
      } else if (prepared.protection === "passphrase" && identity.id !== prepared.identity.id) {
        await saveIdentity(username, explicitLock === "passphrase" ? localUnlock : password, identity);
      }
      localStorage.setItem("jtuc:last-user", session.user.username);
      onAuthenticated(
        session,
        identity,
        prepared.protection === "none" || migrateLegacyProtection ? "automatic" : prepared.protection,
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "REGISTRATION_CLOSED") {
        setRegistrationMode("closed");
        setMode("login");
      }
      setError(caught instanceof Error ? caught.message : "Не удалось войти");
    } finally {
      setBusy(false);
    }
  };

  const requestApproval = async (event: FormEvent) => {
    event.preventDefault();
    if (!deviceLink) return;
    setError(null);
    setBusy(true);
    try {
      const identity = await createIdentity();
      const device = devicePayload(identity);
      const signature = await createDeviceLinkClaimProof(deviceLink.secret, deviceLink.linkId, device);
      const requested = await requestDeviceLinkApproval(deviceLink.linkId, { device, signature });
      setPendingApproval({
        approvalCode: requested.approvalCode,
        claimId: requested.claimId,
        claimToken: requested.claimToken,
        identity,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось запросить подключение");
    } finally {
      setBusy(false);
    }
  };

  const useRegularLogin = () => {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    window.location.reload();
  };

  return (
    <main className="auth-page">
      <section className="auth-intro">
        <div className="brand-mark"><img alt="Личный кабинет вебинаров" src="/logo.png" /></div>
        <p className="eyebrow">ВЕБИНАРЫ</p>
        <h1>Личный кабинет<br />вебинаров.</h1>
        <p className="auth-lead">Сервис находится в разработке. Личный кабинет доступен для участников тестирования.</p>
        <div className="trust-row">
          <span><Construction size={18} /> В разработке</span>
          <span><UserRound size={18} /> Доступ по учётной записи</span>
        </div>
      </section>

      {deviceLink ? (
        <section className="auth-card device-link-auth" aria-labelledby="auth-title">
          <div className="auth-card-head">
            <p className="eyebrow">НОВОЕ УСТРОЙСТВО</p>
            <h2 id="auth-title">Вход по QR-коду</h2>
            <p>Логин и пароль аккаунта вводить не нужно. Подтвердите подключение на уже авторизованном устройстве.</p>
          </div>
          {!pendingApproval ? (
            <>
              <div className="device-link-notice">
                <LockOpen />
                <span>
                  <b>Автоматический вход на этом устройстве</b>
                  <small>После подтверждения вход сохранится на этом устройстве. Дополнительную блокировку можно включить позже в настройках.</small>
                </span>
              </div>
              <form onSubmit={(event) => void requestApproval(event)}>
                {error && <div className="form-error" role="alert">{error}</div>}
                <button className="primary-button" disabled={busy} type="submit">{busy ? "Подготавливаем вход…" : "Запросить подключение"}</button>
              </form>
            </>
          ) : (
            <div className="pairing-wait" aria-live="polite">
              <span className="pairing-loader"><LoaderCircle /></span>
              <b>Подтвердите новое устройство</b>
              <p>На уже авторизованном устройстве откроется запрос. Убедитесь, что на обоих экранах указан одинаковый код:</p>
              <strong aria-label={`Код подтверждения ${pendingApproval.approvalCode}`}>{pendingApproval.approvalCode.slice(0, 3)} {pendingApproval.approvalCode.slice(3)}</strong>
              <small>Шестизначный код разрешает этот вход. Дополнительную локальную блокировку можно включить в настройках после подключения.</small>
              {error && <div className="form-error" role="alert">{error}</div>}
            </div>
          )}
          <button className="text-button" disabled={busy} onClick={useRegularLogin} type="button">Войти обычным способом</button>
          <p className="auth-note"><Link2 /> Подтвердите подключение на устройстве, где личный кабинет уже открыт.</p>
        </section>
      ) : (
        <section className="auth-card" aria-labelledby="auth-title">
          {registrationMode === "open" && <div className="auth-tabs" role="tablist">
            <button aria-controls="auth-panel" aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} id="login-tab" onClick={() => setMode("login")} role="tab" type="button">Вход</button>
            <button aria-controls="auth-panel" aria-selected={mode === "register"} className={mode === "register" ? "active" : ""} id="register-tab" onClick={() => setMode("register")} role="tab" type="button">Регистрация</button>
          </div>}
          <div aria-labelledby={registrationMode === "open" ? mode === "login" ? "login-tab" : "register-tab" : "auth-title"} className="auth-panel" id="auth-panel" role={registrationMode === "open" ? "tabpanel" : undefined}>
          <div className="auth-card-head">
            <h2 id="auth-title">{mode === "login" ? "С возвращением" : "Новый аккаунт"}</h2>
            <p>{mode === "login" ? "Войдите в личный кабинет вебинаров." : "Создайте учётную запись участника."}</p>
          </div>
          {registrationMode === "closed" && <p className="registration-notice">Самостоятельная регистрация закрыта. Учётную запись выдаёт администратор.</p>}
          <form onSubmit={(event) => void submitCredentials(event)}>
            {mode === "register" && <label><span>Отображаемое имя</span><input autoComplete="name" maxLength={80} onChange={(event) => setDisplayName(event.target.value)} required value={displayName} placeholder="Дмитрий" /></label>}
            <label><span>Имя пользователя</span><div className="input-prefix"><b>@</b><input autoCapitalize="none" autoComplete="username" pattern="[A-Za-z0-9_]{3,32}" onChange={(event) => setUsername(event.target.value)} required value={username} placeholder="username" /></div></label>
            <label><span>Пароль</span><input autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={12} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} placeholder="Не менее 12 символов" /></label>
            {mode === "login" && appLockPreference(username) === "passphrase" && <label><span>Локальная защитная фраза</span><input autoComplete="current-password" minLength={12} onChange={(event) => setLocalUnlock(event.target.value)} required type="password" value={localUnlock} placeholder="Только для этого устройства" /></label>}
            {mode === "register" && <label><span>Повторите пароль</span><input autoComplete="new-password" minLength={12} onChange={(event) => setConfirm(event.target.value)} required type="password" value={confirm} /></label>}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="primary-button" disabled={busy} type="submit">{busy ? "Подготавливаем вход…" : mode === "login" ? "Открыть личный кабинет" : "Создать аккаунт"}</button>
          </form>
          <p className="auth-note">Личный кабинет вебинаров находится в разработке.</p>
          </div>
        </section>
      )}
    </main>
  );
}
